/**
 * Cron Expression secici.
 *
 * 2 mod:
 * 1) "Preset" — Onceden tanimli ifadelerden secim (kullanicilar icin onerilen)
 * 2) "Custom" — Manuel "min hour day month dow" girisi (advanced)
 *
 * 5-alanli standart cron formatini destekler (Hangfire/Cronos uyumlu).
 */

import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Stack,
  Alert,
} from '@mui/material';
import { CRON_PRESETS } from '../types';
import type { ReportType } from '../types';

interface Props {
  value: string;
  reportType: ReportType;
  onChange: (cron: string, reportType: ReportType) => void;
}

function validateCron(cron: string): { valid: boolean; message?: string } {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    return { valid: false, message: '5 alan bekleniyor: "dakika saat gün ay haftagünü"' };
  }
  return { valid: true };
}

const CronExpressionPicker: React.FC<Props> = ({ value, reportType, onChange }) => {
  const matchingPreset = CRON_PRESETS.find((p) => p.cron === value && p.reportType === reportType);
  const [mode, setMode] = React.useState<'preset' | 'custom'>(matchingPreset ? 'preset' : 'custom');

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'preset' | 'custom' | null) => {
    if (newMode) setMode(newMode);
  };

  const handlePresetChange = (presetId: string) => {
    const preset = CRON_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      onChange(preset.cron, preset.reportType);
    }
  };

  const handleCustomChange = (cron: string) => {
    onChange(cron, reportType);
  };

  const handleReportTypeChange = (newType: ReportType) => {
    onChange(value, newType);
  };

  const validation = validateCron(value);

  return (
    <Box>
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={handleModeChange}
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="preset">Hazır Zamanlama</ToggleButton>
        <ToggleButton value="custom">Özel Cron</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'preset' ? (
        <FormControl fullWidth size="small">
          <InputLabel>Zamanlama</InputLabel>
          <Select
            label="Zamanlama"
            value={matchingPreset?.id || ''}
            onChange={(e) => handlePresetChange(e.target.value as string)}
          >
            {CRON_PRESETS.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <Stack>
                  <Typography variant="body2">{p.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.description} - cron: {p.cron}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Stack spacing={1}>
          <FormControl fullWidth size="small">
            <InputLabel>Rapor Tipi</InputLabel>
            <Select
              label="Rapor Tipi"
              value={reportType}
              onChange={(e) => handleReportTypeChange(e.target.value as ReportType)}
            >
              <MenuItem value="daily">Günlük</MenuItem>
              <MenuItem value="weekly">Haftalık</MenuItem>
              <MenuItem value="monthly">Aylık</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Cron Expression"
            value={value}
            onChange={(e) => handleCustomChange(e.target.value)}
            size="small"
            fullWidth
            error={!validation.valid}
            helperText={
              validation.valid
                ? 'Format: dakika saat gün ay haftagünü  (örn: "0 22 * * *" → her gün 22:00)'
                : validation.message
            }
          />
        </Stack>
      )}

      {validation.valid && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          <Typography variant="caption">
            Aktif cron: <strong>{value}</strong> ({reportType === 'daily' ? 'Günlük' : reportType === 'weekly' ? 'Haftalık' : 'Aylık'})
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default CronExpressionPicker;
